import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import { EDUCATION_DEFAULTS } from '../../../data/educationDefaults';
import './EducationSubjectsOfferedForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function EducationSubjectsOfferedForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const defaults = EDUCATION_DEFAULTS.subjects_offered;

  const [formData, setFormData] = useState({
    title: defaults.title || '',
    subtitle: defaults.subtitle || '',
    description: defaults.description || '',
    stats: defaults.stats ? [...defaults.stats] : [],
    subjects: defaults.subjects ? [...defaults.subjects] : []
  });

  const [pendingFields, setPendingFields] = useState({});
  const [originalData, setOriginalData] = useState({
    title: defaults.title || '',
    subtitle: defaults.subtitle || '',
    description: defaults.description || '',
    stats: defaults.stats ? [...defaults.stats] : [],
    subjects: defaults.subjects ? [...defaults.subjects] : []
  });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [mediaMap, setMediaMap] = useState({});
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [editingSubjectIndex, setEditingSubjectIndex] = useState(null);
  const [subjectModalData, setSubjectModalData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadMediaMap();
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(`${apiBase}/education/cms/media?_t=${Date.now()}`, { 
        credentials: 'include',
        headers: { 'Cache-Control': 'no-cache' }
      });
      if (!response.ok) return;
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const id = media.id || media.media_id;
          const url = media.url || media.file_path;
          if (id && url) {
            map[id] = url;
            map[String(id)] = url;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('Media fetch error:', err.message);
    }
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      let result = null;
      
      // Try authenticated endpoint first
      console.log('[EducationSubjectsOfferedForm] Trying authenticated endpoint...');
      const adminResponse = await fetch(`${apiBase}/education/cms/sections`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (adminResponse.ok) {
        result = await adminResponse.json();
        console.log('[EducationSubjectsOfferedForm] ✓ Got data from admin endpoint');
      } else if (adminResponse.status === 401) {
        console.log('[EducationSubjectsOfferedForm] Admin endpoint unauthorized, trying public endpoint...');
        // Fallback to public endpoint
        const publicResponse = await fetch(`${apiBase}/education/cms/sections?published=true`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (publicResponse.ok) {
          result = await publicResponse.json();
          console.log('[EducationSubjectsOfferedForm] ✓ Got data from public endpoint');
        } else {
          throw new Error(`Public endpoint failed with status ${publicResponse.status}`);
        }
      } else {
        throw new Error(`Admin endpoint failed with status ${adminResponse.status}`);
      }
      
      if (result.ok && Array.isArray(result.data)) {
        console.log('[EducationSubjectsOfferedForm] Received sections:', result.data.map(s => s.section_type));
        // Try with -shell suffix first, then without
        let section = result.data.find(s => s.section_type === 'education-subjects-offered-shell');
        if (!section) {
          section = result.data.find(s => s.section_type === 'education-subjects-offered');
        }
        if (section) {
          setSectionId(section.id);
          console.log('✓ Loaded subjects section ID:', section.id);
          
          // Check for pending changes
          let pendingFieldsMap = {};
          try {
            const pendingResponse = await fetch(
              `${apiBase}/cms/section/${section.id}/with-pending`,
              { credentials: 'include', headers: { 'Content-Type': 'application/json' } }
            );
            
            if (pendingResponse.ok) {
              const pendingResult = await pendingResponse.json();
              if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
                const { pending_changes } = pendingResult.data;
                if (Array.isArray(pending_changes)) {
                  pending_changes.forEach(change => {
                    if (change.status === 'pending' && change.locale === 'en') {
                      if (change.field_id && change.field_id.includes('subjects.')) {
                        const match = change.field_id.match(/subjects\.(\d+)\./);
                        if (match) {
                          const subjectIdx = parseInt(match[1]);
                          pendingFieldsMap[`subject_${subjectIdx}`] = true;
                        }
                      }
                    }
                  });
                }
              }
            }
          } catch (err) {
            console.warn('[EducationSubjectsOfferedForm] Pending changes check failed:', err.message);
          }
          
          setPendingFields(pendingFieldsMap);
          
          const en = (section.content_published || section.content || {}).en || {};
          const loadedData = {
            title: en.title || defaults.title || '',
            subtitle: en.subtitle || defaults.subtitle || '',
            description: en.description || defaults.description || '',
            stats: Array.isArray(en.stats) ? en.stats : defaults.stats || [],
            subjects: Array.isArray(en.subjects) ? en.subjects : defaults.subjects || []
          };
          setFormData(loadedData);
          setOriginalData(loadedData);
        } else {
          console.error('✗ Subjects section not found in API response');
          console.error('Available section types:', result.data.map(s => s.section_type));
          toast.error('Section not found. Please check with administrator.');
        }
      } else {
        console.error('✗ Invalid API response:', result);
        toast.error('API error loading section');
      }
    } catch (err) {
      console.error('Error loading section:', err.message);
      toast.error('Failed to load section');
    } finally {
      setLoading(false);
    }
  };

  const saveSection = async (updateData) => {
    if (!sectionId) {
      toast.error('Section ID not available');
      return false;
    }
    try {
      // Use PUT endpoint which creates cms_pending_changes record for approval workflow
      const response = await fetch(`${apiBase}/education/cms/section/${sectionId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
        body: JSON.stringify({
          content_published: JSON.stringify({ en: updateData })
        })
      });
      
      if (response.status === 401) {
        toast.error('Session expired. Please login again.');
        return false;
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error:', response.status, errorText);
        throw new Error(`Save failed: ${response.status}`);
      }
      
      const result = await response.json();
      if (result.success || result.ok) {
        toast.success('✓ Changes submitted for approval');
      }
      return result.success || result.ok;
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save: ' + err.message);
      return false;
    }
  };

  const saveOverview = async () => {
    setSubmitting(prev => ({ ...prev, 'overview': true }));
    try {
      const success = await saveSection(formData);
      if (success) {
        // Update local state to reflect the new data
        console.log('[EducationSubjectsOfferedForm] Overview saved successfully');
      } else {
        throw new Error('Save failed');
      }
    } catch (err) {
      console.error('Error saving overview:', err);
      toast.error('Failed to save: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, 'overview': false }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error(`Invalid image type. Allowed: JPEG, PNG, WebP. Got: ${file.type}`);
      return;
    }
    
    // Validate file size
    if (file.size > MAX_IMAGE_SIZE) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      toast.error(`Image too large: ${sizeMB}MB. Max 5MB`);
      return;
    }

    setImageUploading(prev => ({ ...prev, 'current': true }));
    setImageProgress(prev => ({ ...prev, 'current': 0 }));

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) setImageProgress(prev => ({ ...prev, 'current': Math.round((e.loaded / e.total) * 100) }));
      });
      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id;
              if (mediaId) {
                // Force string to ensure consistent type in database
                const mediaIdStr = String(mediaId);
                setSubjectModalData(prev => ({...prev, image: mediaIdStr}));
                console.log('[EducationSubjectsOfferedForm] Image uploaded: mediaId=' + mediaIdStr);
                toast.success('✓ Image uploaded');
              }
            }
          } catch (e) {
            toast.error('Invalid response');
          }
        }
        setImageUploading(prev => ({ ...prev, 'current': false }));
      });
      xhr.addEventListener('error', () => {
        toast.error('Upload failed');
        setImageUploading(prev => ({ ...prev, 'current': false }));
      });
      xhr.open('POST', `${apiBase}/education/cms/media/upload`);
      xhr.withCredentials = true;
      xhr.send(formDataUpload);
    } catch (err) {
      setImageUploading(prev => ({ ...prev, 'current': false }));
    }
  };

  const editSubject = (index) => {
    setEditingSubjectIndex(index);
    setSubjectModalData({...formData.subjects[index]});
  };

  const saveSubject = async () => {
    if (!subjectModalData?.name) {
      toast.warning('Please fill name');
      return;
    }
    const key = `subject_${editingSubjectIndex}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));
    try {
      const updated = { ...formData };
      updated.subjects[editingSubjectIndex] = subjectModalData;
      
      console.log('[EducationSubjectsOfferedForm] Saving subject:', {
        index: editingSubjectIndex,
        name: subjectModalData.name,
        hasPoints: !!subjectModalData.points,
        pointsCount: subjectModalData.points?.length || 0,
        points: subjectModalData.points,
        image: subjectModalData.image
      });
      
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `subjects.${editingSubjectIndex}.update`,
            field_name: `Update Subject ${editingSubjectIndex + 1}: ${subjectModalData.name}`,
            old_value: originalData.subjects[editingSubjectIndex] || {},
            new_value: subjectModalData,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Subject ${editingSubjectIndex + 1} submitted for approval`);
        
        setPendingFields(prev => ({
          ...prev,
          [`subject_${editingSubjectIndex}`]: true
        }));

        setFormData(updated);
        setOriginalData(prev => ({
          ...prev,
          subjects: prev.subjects.map((s, i) => i === editingSubjectIndex ? subjectModalData : s)
        }));

        setEditingSubjectIndex(null);
        setSubjectModalData(null);
      } else {
        if (response.status === 409) {
          toast.error(`Subject ${editingSubjectIndex + 1} already has a pending approval`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      console.error('Error saving subject:', err);
      toast.error('Failed to save: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  const updateSubjectPoint = (idx, value) => {
    if (!subjectModalData.points) subjectModalData.points = [];
    const updated = [...subjectModalData.points];
    updated[idx] = value;
    setSubjectModalData(prev => ({...prev, points: updated}));
  };

  if (loading) return <div className="education-subjects-form-container"><div className="hero-form-wrapper"><div className="loading"><div className="loading-spinner"></div></div></div></div>;

  return (
    <div className="education-subjects-form-container">
      <ToastContainer />
      <div className="hero-form-wrapper">
        <div className="hero-form-header">
          <h2 className="hero-form-title">{formData.title}</h2>
          <p className="hero-form-description">{formData.subtitle}</p>
        </div>

        <div className="tabs-wrapper">
          <div className="tabs-nav">
            <button className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
            <button className={`tab-button ${activeTab === 'subjects' ? 'active' : ''}`} onClick={() => setActiveTab('subjects')}>Subjects</button>
          </div>
        </div>

        {activeTab === 'overview' && (
          <div className="form-section">
            <h3 className="form-section-title">Section Overview</h3>
            <div className="form-field-card">
              <label>Title</label>
              <input type="text" className="text-input" value={formData.title} onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))} maxLength="100" />
            </div>
            <div className="form-field-card">
              <label>Subtitle</label>
              <input type="text" className="text-input" value={formData.subtitle} onChange={(e) => setFormData(prev => ({...prev, subtitle: e.target.value}))} maxLength="200" />
            </div>
            <div className="form-field-card">
              <label>Description</label>
              <textarea className="textarea-input" value={formData.description} onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))} rows={3} maxLength="500" />
            </div>
            <div style={{marginTop: '24px'}}>
              <h4 style={{fontSize: '14px', fontWeight: 700, marginBottom: '16px'}}>Statistics</h4>
              {formData.stats?.map((stat, idx) => (
                <div key={idx} className="form-field-card" style={{marginBottom: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
                  <div>
                    <label style={{fontSize: '12px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', marginBottom: '8px', display: 'block'}}>Value</label>
                    <input type="text" className="text-input" value={stat.value} onChange={(e) => {
                      const updated = [...formData.stats];
                      updated[idx].value = e.target.value;
                      setFormData(prev => ({...prev, stats: updated}));
                    }} maxLength="50" />
                  </div>
                  <div>
                    <label style={{fontSize: '12px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', marginBottom: '8px', display: 'block'}}>Label</label>
                    <input type="text" className="text-input" value={stat.label} onChange={(e) => {
                      const updated = [...formData.stats];
                      updated[idx].label = e.target.value;
                      setFormData(prev => ({...prev, stats: updated}));
                    }} maxLength="100" />
                  </div>
                </div>
              ))}
            </div>
            <button className="submit-btn" onClick={saveOverview} disabled={submitting['overview']}>
              {submitting['overview'] ? 'Saving...' : 'Save Overview'}
            </button>
          </div>
        )}

        {activeTab === 'subjects' && (
          <div>
            <div className="form-section">
              <h3 className="form-section-title">Subjects List</h3>
              {formData.subjects.length === 0 && (
                <div className="items-empty-state">
                  <div className="empty-icon">📚</div>
                  <h4>No subjects added</h4>
                </div>
              )}
              {formData.subjects.length > 0 && (
                <div className="level-cards-grid">
                  {formData.subjects.map((subject, idx) => (
                    <button key={idx} type="button" className="level-card" onClick={() => editSubject(idx)}>
                      <div className="level-card-image">
                        {subject.image && mediaMap[subject.image] ? (
                          <img src={mediaMap[subject.image]} alt={subject.name} />
                        ) : (
                          <div className="level-card-image-placeholder">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                              <circle cx="8.5" cy="8.5" r="1.5"></circle>
                              <polyline points="21 15 16 10 5 21"></polyline>
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="level-card-content">
                        <div className="level-card-header">
                          <h4 className="level-card-title">{subject.name}</h4>
                          {pendingFields[`subject_${idx}`] && (
                            <span className="level-card-badge">⏳</span>
                          )}
                        </div>
                        <p className="level-card-desc">{subject.description.substring(0, 60)}...</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {editingSubjectIndex !== null && subjectModalData && (
        <div className="modal-overlay" onClick={() => {setEditingSubjectIndex(null); setSubjectModalData(null);}}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit {subjectModalData.name}</h3>
              <button className="modal-close" onClick={() => {setEditingSubjectIndex(null); setSubjectModalData(null);}}>×</button>
            </div>
            <div className="modal-body">
              <div className="modal-field">
                <label>Subject Image</label>
                {subjectModalData.image && mediaMap[subjectModalData.image] && (
                  <div className="modal-image-preview">
                    <img src={mediaMap[subjectModalData.image]} alt={subjectModalData.name} />
                  </div>
                )}
                <div className="image-upload-area">
                  <input type="file" accept="image/*" onChange={handleImageUpload} disabled={imageUploading['current']} className="image-input" id="subject-image" />
                  <label htmlFor="subject-image" className="upload-label">
                    {imageUploading['current'] ? `Uploading ${imageProgress['current']}%...` : '📤 Upload Image'}
                  </label>
                </div>
              </div>
              <div className="modal-field">
                <label>Subject Name</label>
                <input type="text" className="modal-input" value={subjectModalData.name} onChange={(e) => setSubjectModalData(prev => ({...prev, name: e.target.value}))} maxLength="100" />
              </div>
              <div className="modal-field">
                <label>Description</label>
                <textarea className="modal-textarea" value={subjectModalData.description} onChange={(e) => setSubjectModalData(prev => ({...prev, description: e.target.value}))} rows={3} />
              </div>
              <div className="modal-field">
                <label>Featured Focus Points (Max 3)</label>
                {subjectModalData.points?.map((point, idx) => (
                  <div key={idx} style={{display: 'flex', gap: '8px', marginBottom: '8px'}}>
                    <input type="text" className="modal-input" value={point} onChange={(e) => updateSubjectPoint(idx, e.target.value)} placeholder="Focus point..." />
                    <button onClick={() => {
                      const updated = subjectModalData.points.filter((_, i) => i !== idx);
                      setSubjectModalData(prev => ({...prev, points: updated}));
                    }} style={{width: '32px', padding: 0, background: '#fed7d7', color: '#c53030', border: 'none', borderRadius: '6px', cursor: 'pointer'}}>✕</button>
                  </div>
                ))}
                {(!subjectModalData.points || subjectModalData.points.length < 3) && (
                  <button onClick={() => {
                    setSubjectModalData(prev => ({...prev, points: [...(prev.points || []), '']}));
                  }} style={{marginTop: '8px', padding: '8px 12px', background: 'transparent', border: '1px dashed #4299e1', borderRadius: '6px', color: '#4299e1', fontSize: '13px', fontWeight: 600, cursor: 'pointer'}}>
                    + Add Point
                  </button>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => {setEditingSubjectIndex(null); setSubjectModalData(null);}}>Cancel</button>
              <button className="btn-submit" onClick={saveSubject} disabled={submitting[`subject_${editingSubjectIndex}`] || pendingFields[`subject_${editingSubjectIndex}`]}>
                {submitting[`subject_${editingSubjectIndex}`] ? 'Saving...' : pendingFields[`subject_${editingSubjectIndex}`] ? 'Pending Approval' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
